import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Banknote,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  RefreshCw,
  User,
  Building2,
  ShieldCheck,
} from 'lucide-react';
import { CourierWithdrawalItem } from '../../types';
import {
  fetchCashierCourierWithdrawals,
  confirmCashierCourierWithdrawal,
  resolveOutletDisplayName,
} from '../../utils/courierWallet';

interface CashierCourierWithdrawalManagerProps {
  cashierUsername?: string;
  cashierOutletId?: string;
  role?: 'outlet_admin' | 'super_admin' | string;
}

export const CashierCourierWithdrawalManager: React.FC<CashierCourierWithdrawalManagerProps> = ({
  cashierUsername,
  cashierOutletId,
  role,
}) => {
  const [withdrawals, setWithdrawals] = useState<CourierWithdrawalItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'PAID' | 'CANCELLED'>('ALL');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Confirmation dialog state before cash payout
  const [confirmTarget, setConfirmTarget] = useState<CourierWithdrawalItem | null>(null);
  const [selectedPayoutOutletId, setSelectedPayoutOutletId] = useState<string>(
    cashierOutletId && cashierOutletId !== 'all' ? cashierOutletId : 'sudirman'
  );
  const [confirming, setConfirming] = useState<boolean>(false);

  const loadWithdrawals = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        const list = await fetchCashierCourierWithdrawals({
          search: searchQuery,
          status: statusFilter,
        });
        setWithdrawals(list);
        setErrorMsg(null);
      } catch (err: any) {
        if (!silent) {
          setErrorMsg(err?.message || 'Gagal memuat daftar pencairan kurir.');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [searchQuery, statusFilter]
  );

  useEffect(() => {
    loadWithdrawals(false);
    const timer = setInterval(() => loadWithdrawals(true), 10000);
    return () => clearInterval(timer);
  }, [loadWithdrawals]);

  const formatRupiah = (amount: number) => `Rp${Number(amount || 0).toLocaleString('id-ID')}`;

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const handleOpenConfirmDialog = (item: CourierWithdrawalItem) => {
    setErrorMsg(null);
    setSelectedPayoutOutletId(
      cashierOutletId && cashierOutletId !== 'all'
        ? cashierOutletId
        : item.targetOutletId && item.targetOutletId !== 'ANY'
        ? item.targetOutletId
        : 'sudirman'
    );
    setConfirmTarget(item);
  };

  const handleConfirmPayment = async () => {
    if (!confirmTarget || confirming) return;
    setConfirming(true);
    setErrorMsg(null);

    try {
      const res = await confirmCashierCourierWithdrawal(
        confirmTarget.id,
        role === 'super_admin' ? selectedPayoutOutletId : cashierOutletId
      );

      setWithdrawals((prev) =>
        prev.map((w) => (w.id === confirmTarget.id ? res.withdrawal : w))
      );
      setConfirmTarget(null);
      setSuccessMsg(
        `Pembayaran tunai sebesar ${formatRupiah(res.withdrawal.amount)} untuk ${res.withdrawal.courierName} (#${res.withdrawal.transactionCode}) berhasil dikonfirmasi sebagai PAID.`
      );
      setTimeout(() => setSuccessMsg(null), 6000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Gagal mengonfirmasi pembayaran pencairan kurir.');
    } finally {
      setConfirming(false);
    }
  };

  const pendingCount = withdrawals.filter((w) => w.status === 'PENDING').length;
  const pendingTotal = withdrawals
    .filter((w) => w.status === 'PENDING')
    .reduce((sum, w) => sum + w.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0A5C99] text-xs font-extrabold uppercase tracking-wider">
            <Banknote className="w-3.5 h-3.5" />
            <span>Menu Kasir • Pencairan Saldo Kurir</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 mt-2">
            Verifikasi & Konfirmasi Pencairan Kurir
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">
            Cocokkan <strong>ID Transaksi</strong> dari Bukti Pencairan Digital kurir, serahkan uang tunai, lalu klik{' '}
            <strong>KONFIRMASI PEMBAYARAN</strong>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-right">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800">
              Menunggu Pembayaran ({pendingCount})
            </p>
            <p className="text-lg font-black text-amber-900">{formatRupiah(pendingTotal)}</p>
          </div>
          <button
            type="button"
            onClick={() => loadWithdrawals(true)}
            disabled={refreshing}
            className="p-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            title="Segarkan Data"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-[#0A5C99]' : ''}`} />
          </button>
        </div>
      </div>

      {/* Alerts */}
      {errorMsg && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3 text-rose-900">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs sm:text-sm font-semibold">{errorMsg}</div>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            className="text-xs font-bold text-rose-700 hover:underline"
          >
            Tutup
          </button>
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-start gap-3 text-emerald-900">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs sm:text-sm font-semibold">{successMsg}</div>
        </div>
      )}

      {/* Search & Status Filters */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari berdasarkan ID Transaksi (contoh: WD-LTN-...) atau Nama Kurir..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 text-xs sm:text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A5C99]"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {(
            [
              { id: 'ALL', label: 'Semua' },
              { id: 'PENDING', label: 'Menunggu Pembayaran' },
              { id: 'PAID', label: 'Sudah Dibayar' },
              { id: 'CANCELLED', label: 'Dibatalkan' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setStatusFilter(tab.id)}
              className={`px-3.5 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider whitespace-nowrap transition-all ${
                statusFilter === tab.id
                  ? 'bg-[#0A5C99] text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Withdrawal Requests List */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">
            Memuat daftar pengajuan pencairan kurir...
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="p-12 text-center">
            <Banknote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-bold text-slate-700">Tidak Ada Transaksi Pencairan</p>
            <p className="text-xs text-slate-500 mt-1">
              Belum ada pengajuan pencairan kurir yang sesuai dengan pencarian/filter saat ini.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {withdrawals.map((wd) => {
              const isPending = wd.status === 'PENDING';
              const isPaid = wd.status === 'PAID';

              return (
                <div
                  key={wd.id}
                  className="p-5 hover:bg-slate-50/70 transition-colors flex flex-col lg:flex-row lg:items-center justify-between gap-4"
                >
                  <div className="space-y-2">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="font-mono text-sm font-black text-[#0A5C99] bg-blue-50 px-3 py-1 rounded-xl border border-blue-200">
                        {wd.transactionCode}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-extrabold px-3 py-1 rounded-full uppercase ${
                          isPending
                            ? 'bg-amber-100 text-amber-800'
                            : isPaid
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-rose-100 text-rose-800'
                        }`}
                      >
                        {isPending && <Clock className="w-3.5 h-3.5" />}
                        {isPaid && <CheckCircle2 className="w-3.5 h-3.5" />}
                        {wd.status === 'CANCELLED' && <XCircle className="w-3.5 h-3.5" />}
                        <span>
                          {isPending
                            ? 'MENUNGGU PEMBAYARAN'
                            : isPaid
                            ? 'PAID (SUDAH DIBAYAR)'
                            : 'DIBATALKAN'}
                        </span>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs text-slate-600 pt-1">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span>
                          Kurir: <strong className="text-slate-900">{wd.courierName}</strong> (@
                          {wd.courierUsername})
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span>
                          Outlet Tujuan:{' '}
                          <strong className="text-slate-800">
                            {resolveOutletDisplayName(wd.targetOutletId || 'ANY')}
                          </strong>
                        </span>
                      </div>

                      <div>
                        Waktu Pengajuan:{' '}
                        <strong className="text-slate-800">{formatDateTime(wd.requestedAt)}</strong>
                      </div>
                    </div>

                    {isPaid && (
                      <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl px-3 py-2 inline-block">
                        Dibayar Tunai di <strong>{wd.paidAtOutletName || wd.paidAtOutletId}</strong> oleh{' '}
                        <strong>{wd.paidByCashierName || wd.paidByCashierUsername}</strong> pada{' '}
                        {formatDateTime(wd.paidAt)}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between lg:justify-end gap-4 pt-3 lg:pt-0 border-t lg:border-t-0 border-slate-100">
                    <div className="text-left lg:text-right">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Nominal Tunai
                      </p>
                      <p className="text-xl font-black text-slate-900">
                        {formatRupiah(wd.amount)}
                      </p>
                    </div>

                    {isPending && (
                      <button
                        type="button"
                        onClick={() => handleOpenConfirmDialog(wd)}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold uppercase tracking-wider shadow-sm transition-all cursor-pointer"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span>KONFIRMASI PEMBAYARAN</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mandatory Confirmation Dialog Before Cash Handover */}
      {confirmTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-black text-slate-900 uppercase">
                  Konfirmasi Serah Terima Uang Tunai
                </h3>
                <p className="text-xs text-slate-500">
                  Tindakan ini bersifat permanen dan tidak dapat dibatalkan
                </p>
              </div>
            </div>

            <div className="mt-4 bg-slate-50 rounded-2xl p-4 space-y-2.5 text-xs border border-slate-200">
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">ID Transaksi</span>
                <span className="font-mono font-black text-[#0A5C99]">
                  {confirmTarget.transactionCode}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Nama Kurir</span>
                <span className="font-bold text-slate-900">{confirmTarget.courierName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500 font-medium">Metode</span>
                <span className="font-bold text-slate-900">TUNAI (KASIR)</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="text-slate-700 font-extrabold">Nominal Diserahkan</span>
                <span className="text-lg font-black text-emerald-700">
                  {formatRupiah(confirmTarget.amount)}
                </span>
              </div>
            </div>

            {role === 'super_admin' && (
              <div className="mt-4">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Outlet Tempat Pembayaran Tunai
                </label>
                <select
                  value={selectedPayoutOutletId}
                  onChange={(e) => setSelectedPayoutOutletId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs font-bold text-slate-800"
                >
                  <option value="sudirman">Chapter 5 - Leton Sudirman</option>
                  <option value="kelakap_7">Chapter 6 - Leton Kelakap 7</option>
                  <option value="letgo-mpp">Let&apos;GO - MPP & Mobile</option>
                </select>
              </div>
            )}

            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 leading-relaxed">
              Pastikan Anda <strong>telah menyerahkan uang tunai sebesar {formatRupiah(confirmTarget.amount)}</strong> secara langsung kepada kurir{' '}
              <strong>{confirmTarget.courierName}</strong> sebelum menekan tombol konfirmasi di bawah ini.
            </div>

            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                disabled={confirming}
                onClick={() => setConfirmTarget(null)}
                className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={confirming}
                onClick={handleConfirmPayment}
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold uppercase tracking-wider shadow-md disabled:opacity-50"
              >
                {confirming ? 'Memproses...' : 'Ya, Sudah Dibayar Tunai'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
